import { useState } from 'react'
import { SkeletonBoxes } from '../skeleton/Skeleton'
import { AddressesHeader } from './AddressesHeader'
import { AddressesGrid } from './AddressesGrid'
import { useDebounce } from '../../utils/hooks/useDebounce'

// Default options that are the same across all apps
const DEFAULT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REQUESTED', label: 'Requested' },
]

const DEFAULT_CHAIN_OPTIONS = [
  { value: 'all', label: 'All Chains' },
  { value: 'BTC', label: 'BTC' },
  { value: 'ETH', label: 'ETH' },
  { value: 'BSC', label: 'BSC' },
  { value: 'POLYGON', label: 'Polygon' },
  { value: 'SOLANA', label: 'Solana' },
  { value: 'TRON', label: 'TRON' },
]

export interface AddressesTabViewProps {
  companyId?: string
  searchTerm?: string
  addresses: Array<{
    id: string
    address: string
    label?: string | null
    chain: string
    status: string
    decisionBy?: string | null
    requester?: {
      id: string
      email: string
      username: string
    } | null
  }>
  loading?: boolean
  error?: Error | null
  onApprove?: (id: string) => void
  onReject?: (id: string) => void
  onManage?: (address: any) => void
  onAddClick: () => void
  showApproveButton?: (status: string) => boolean
  showRejectButton?: (status: string) => boolean
  showManageButton?: (status: string) => boolean
  statusOptions?: Array<{ value: string; label: string }>
  chainOptions?: Array<{ value: string; label: string }>
  gridLayout?: 'default' | 'compact'
  useTabsHeader?: boolean
  title?: string
  addButtonText?: string
  className?: string
}

export function AddressesTabView({
  companyId: _companyId,
  searchTerm,
  addresses,
  loading = false,
  error,
  onApprove,
  onReject,
  onManage,
  onAddClick,
  showApproveButton,
  showRejectButton,
  showManageButton,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  chainOptions = DEFAULT_CHAIN_OPTIONS,
  gridLayout = 'default',
  title = 'Whitelisted Addresses',
  addButtonText = 'Add',
  className = '',
}: AddressesTabViewProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [chainFilter, setChainFilter] = useState<string>('all')

  const debouncedSearchTerm = useDebounce(searchTerm)

  // Filter addresses based on search term, status, and chain
  const filteredAddresses = addresses.filter((address) => {
    // Search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase()
      const matchesSearch =
        address.address.toLowerCase().includes(searchLower) ||
        address.label?.toLowerCase().includes(searchLower) ||
        address.chain.toLowerCase().includes(searchLower)
      if (!matchesSearch) return false
    }

    // Status filter
    if (statusFilter !== 'all' && address.status !== statusFilter) {
      return false
    }

    // Chain filter
    if (chainFilter !== 'all' && address.chain !== chainFilter) {
      return false
    }

    return true
  })

  if (error || loading) {
    return (
      <div className="space-y-4">
        <AddressesHeader
          title={title}
          onAddClick={onAddClick}
          addButtonText={addButtonText}
          statusFilter={statusFilter}
          chainFilter={chainFilter}
          onStatusFilterChange={setStatusFilter}
          onChainFilterChange={setChainFilter}
          statusOptions={statusOptions}
          chainOptions={chainOptions}
        />
        {error ? <div>Error loading addresses</div> : <SkeletonBoxes />}
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <AddressesHeader
        title={title}
        onAddClick={onAddClick}
        addButtonText={addButtonText}
        statusFilter={statusFilter}
        chainFilter={chainFilter}
        onStatusFilterChange={setStatusFilter}
        onChainFilterChange={setChainFilter}
        statusOptions={statusOptions}
        chainOptions={chainOptions}
      />

      <AddressesGrid
        addresses={filteredAddresses}
        onApprove={onApprove}
        onReject={onReject}
        onManage={onManage}
        showApproveButton={showApproveButton}
        showRejectButton={showRejectButton}
        showManageButton={showManageButton}
        gridLayout={gridLayout}
      />
    </div>
  )
}
